import React from 'react';
import { styled } from '@mui/material/styles';
import {
  Box,
  useMediaQuery,
  useTheme,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  DialogContentText,
  Button,
  Snackbar,
  Alert,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import SignupForm from '../../components/auth/SignupForm';
import authService from '../../services/authService';

const PREFIX = 'Signup';

const classes = {
  container: `${PREFIX}-container`,
  content: `${PREFIX}-content`,
  left: `${PREFIX}-left`,
  right: `${PREFIX}-right`,
  center: `${PREFIX}-center`,
  logo1: `${PREFIX}-logo1`,
  logo2: `${PREFIX}-logo2`,
  logo3: `${PREFIX}-logo3`,
  logo4: `${PREFIX}-logo4`,
};

const Root = styled('div')(({ theme }) => ({
  [`&.${classes.container}`]: {
    display: 'flex',
    flexDirection: 'row',
    flex: '0 1 100%',
    backgroundColor: theme.palette.info,
    width: '100%',
    height: '100%',
    backgroundSize: 'cover',
    [theme.breakpoints.down('sm')]: {
      backgroundImage: 'url(/background_small.png)',
    },
    [theme.breakpoints.up('sm')]: {
      backgroundImage: 'url(/background.png)',
    },
  },

  [`& .${classes.left}`]: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    marginTop: 'auto',
    marginBottom: 'auto',
    marginRight: 65,
    alignItems: 'end',
  },

  [`& .${classes.right}`]: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    marginTop: 'auto',
    marginBottom: 'auto',
    marginLeft: 65,
    alignItems: 'left',
  },

  [`& .${classes.center}`]: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    marginTop: 'auto',
    marginBottom: 'auto',
    alignItems: 'center',
  },

  [`& .${classes.logo1}`]: {
    width: 400,
    marginBottom: 19,
  },

  [`& .${classes.logo2}`]: {
    width: 230,
    marginTop: 18,
    marginRight: 85,
  },

  [`& .${classes.logo3}`]: {
    width: 163,
    marginBottom: 19,
  },

  [`& .${classes.logo4}`]: {
    width: 137,
    marginTop: 18,
  },
}));

export default function Signup() {
  const { t } = useTranslation('common');
  const theme = useTheme();
  const isLarge = useMediaQuery(theme.breakpoints.up('md'));
  const navigate = useNavigate();
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [snackbarOpen, setSnackbarOpen] = React.useState(false);
  const [error, setError] = React.useState<String>('');

  const closeDialog = () => {
    setDialogOpen(false);
    navigate('/login');
  };

  const signup = (info: any) => {
    authService
      .signup(info.Email, info.Password)
      .then((user) => {
        authService.confirmSignUp(info.Email).subscribe({
          next() {
            setDialogOpen(true);
          },
          error(err: any) {
            setError(err);
            setSnackbarOpen(true);
          },
        });
      })
      .catch((err: String) => {
        setError(err);
        setSnackbarOpen(true);
      });
  };

  return (
    <Root className={classes.container}>
      {isLarge && (
        <Box className={classes.left}>
          <img alt="Molina" src="/logo.png" className={classes.logo1} />
        </Box>
      )}
      {isLarge && (
        <Box className={classes.right}>
          <SignupForm submit={signup} />
        </Box>
      )}
      {!isLarge && (
        <Box className={classes.center}>
          <img alt="Molina" src="/logo.png" className={classes.logo3} />
          <SignupForm submit={signup} />
        </Box>
      )}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        onClose={() => setSnackbarOpen(false)}>
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity="error"
          sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>
      <Dialog open={dialogOpen} disableEscapeKeyDown={true}>
        <DialogTitle>{t('auth.createYourAccount')}</DialogTitle>
        <DialogContent>
          <DialogContentText color="textPrimary">
            {t('auth.signupSuccessMessage')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            color="primary"
            onClick={() => closeDialog()}>
            {t('common.ok')}
          </Button>
        </DialogActions>
      </Dialog>
    </Root>
  );
}
